import { observable, action, computed, makeObservable } from 'mobx';
import _ from 'lodash';

export class ParentAttestation {
  constructor(data, root) {
    this._root = root;
    this.id = data.id;
    this.parent_guidance_id = data.parent_guidance_id;
    this.parent_id = data.parent_id;
    this.potential_child_id = data.potential_child_id;
    this.skill_recommendation_id = data.skill_recommendation_id;
    this.inserted_at = data.inserted_at;
  }

  get isAcademic() {
    return !!this.parentGuidance.skill_id;
  }

  get isPowerSkill() {
    return !!this.parentGuidance.power_skill_parent_curriculum;
  }

  get guidanceType() {
    return this.isAcademic ? 'academic' : 'power';
  }

  get parentGuidance() {
    return this._root.ParentGuidanceStore.find(this.parent_guidance_id);
  }

  get skillLevel() {
    return this.parentGuidance.skillLevel;
  }

  get potentialChild() {
    return this._root.PotentialChildStore.find(this.potential_child_id);
  }

  get attestor() {
    return this._root.ParentStore.find(this.parent_id);
  }
}

export default class ParentAttestationStore {
  constructor(root) {
    makeObservable(this, {
      data: observable,
      add: action,
      addMany: action,
      remove: action,
      all: computed,
      reset: action,
    })

    this.root = root;
  }

  data = new Map();

  add = (data) => {
    const attestation = new ParentAttestation(data, this.root);
    this.data.set(attestation.id, attestation);
  }

  addMany = (datas) => {
    const merge = new Map();
    datas.forEach(data => {
      const attestation = new ParentAttestation(data, this.root);
      merge.set(attestation.id, attestation);
    })
    this.data.merge(merge);
  }

  remove = (id) => {
    if (id && this.data.has(id)) this.data.delete(id);
  }

  find(id) {
    return this.data.get(id);
  }

  get all() {
    return Array.from(this.data.values());
  }

  get valid() {
    return _.filter(this.all, attestation =>
      attestation.potentialChild &&
        attestation.parentGuidance
    );
  }

  attestationsForPotentialChild(potentialChildId) {
    return _.chain(this.valid)
            .filter(attestation => attestation.potential_child_id === potentialChildId)
            .sortBy('inserted_at')
            .reverse()
            .value();
  }

  /**
   * Same as `attestationsForPotentialChild(potentialChildId)`, but further filtering by a `filters` object.
   *
   * @param {integer} potentialChildId - The numerical ID of the Potential Child to query by.
   * @param {object} filters - The filters to apply. Note only the following are used:
   * @param {string} filters.type - The type of activity this attestation applies to, either 'academic' or 'power'.
   * @param {integer} filters.subject - If `type === 'academic'`, the numerical ID of the Academic Subject to filter by.
   * @param {integer} filters.domain - If `type === 'academic'`, the numerical ID of the Academic Domain to filter by.
   * @param {string} filters.grade_level - If `type === 'academic'`, the Academic grade level to filter by.
   * @param {integer} filters.category - If `type === 'power'`, the numerical ID of the Power Skill Category to filter by.
   * @param {string} filters.gradeBand - If `type === 'power'`, the grade band of the Power Skill Level's guidance to filter by.
   */
  attestationsForPotentialChildByFilter(potentialChildId, filters) {
    let resultSet = this.attestationsForPotentialChild(potentialChildId);
    if (filters.type && filters.type === 'academic') {
      resultSet = _.filter(resultSet, attestation => attestation.isAcademic);
      if (filters.subject) {
        resultSet = _.filter(resultSet, ['skillLevel.domain.subject.id', filters.subject])
      }
      if (filters.domain) {
        resultSet = _.filter(resultSet, ['skillLevel.domain.id', filters.domain])
      }
      if (filters.gradeLevel) {
        resultSet = _.filter(resultSet, ['skillLevel.gradeLevel', `${filters.gradeLevel}`])
      }
    } else if (filters.type && filters.type === 'power') {
      resultSet = _.filter(resultSet, attestation => attestation.isPowerSkill)
      if (filters.category) {
        resultSet = _.filter(resultSet, ['skillLevel.powerSkill.category.id', filters.category])
      }
      if (filters.gradeBand) {
        resultSet = _.filter(resultSet, attestation => _.some(_.flatMap(attestation.skillLevel.levels, 'parent_guidances'), ['grade.display', filters.gradeBand]));
      }
    }
    return resultSet
  }

  attestationsFor(potentialChildId, guidanceId) {
    return _.filter(this.valid, attestation => attestation.potential_child_id === potentialChildId && attestation.parent_guidance_id === guidanceId);
  }

  attestationsForGuidance(guidanceId) {
    return _.filter(this.valid, attestation => attestation.parent_guidance_id === guidanceId);
  }

  reset = () => {
    this.data.clear();
  }
}
